import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PieChart } from "@mui/x-charts/PieChart";

import ChartCard from "./chartCard.component";
import { buildChartSx, useChartPalette } from "./chartPalette";

export interface DistributionItem {
  label: string;
  value: number;
  /** True for the single bucket the backend folded the long tail into. */
  is_other: boolean;
}

export interface Distribution {
  items: DistributionItem[];
  total: number;
  /** How many entries the "Other" slice stands for; 0 when nothing was folded. */
  other_count: number;
}

interface DistributionPieProps {
  title: string;
  subtitle?: string;
  distribution?: Distribution | null;
  /** Renders one slice's value the way its unit reads (zł, pages, jobs...). */
  valueFormatter: (value: number) => string;
  loading?: boolean;
  emptyMessage?: string;
  height?: number;
  /**
   * Overrides the categorical assignment, for pies whose slices carry a
   * reserved meaning (good/bad) rather than identity. Indexed like `items`;
   * a missing entry falls back to the categorical slot.
   */
  sliceColors?: string[];
  /** Rendered on the title row - a metric switch, a total, a filter. */
  action?: ReactNode;
}

/**
 * Legend entries sit in a fixed-width column beside the plot, so a long part
 * name has to be cut rather than pushed off the card. The full name is still
 * on the slice's tooltip.
 */
const truncateLegendLabel = (label: string, max = 26) =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

/**
 * One pie of a "what is this made of" breakdown.
 *
 * The backend hands over at most five named slices plus an "Other" bucket -
 * past about six segments neighbouring slices stop being tellable apart and the
 * legend becomes a list to read rather than a key to glance at. The full
 * ranking is always in the breakdown table under the chart, which is also what
 * makes the few sub-3:1 palette slots legal here: no value is reachable only by
 * colour.
 */
export default function DistributionPie({
  title,
  subtitle,
  distribution,
  valueFormatter,
  loading = false,
  emptyMessage,
  height = 300,
  sliceColors,
  action,
}: DistributionPieProps) {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const chartSx = useMemo(() => buildChartSx(palette), [palette]);

  // Memoised rather than defaulted inline: a fresh [] on every render would
  // re-run the slice mapping below it.
  const items = useMemo(() => distribution?.items ?? [], [distribution]);
  const total = distribution?.total ?? 0;

  const data = useMemo(
    () =>
      items.map((item, index) => {
        // "Other" is explicitly "everything else", so it recedes into the
        // de-emphasis grey instead of taking a categorical hue that would make
        // it look like one more named category.
        const color = item.is_other
          ? palette.muted
          : (sliceColors?.[index] ?? palette.series(index));
        const label = item.is_other
          ? `${t("Other")} (${distribution?.other_count ?? 0})`
          : item.label || "-";

        return {
          id: `${index}-${item.label}`,
          value: item.value,
          color,
          label: (location: "tooltip" | "legend" | "arc") =>
            location === "legend" ? truncateLegendLabel(label) : label,
        };
      }),
    [items, palette, t, distribution, sliceColors]
  );

  const share = (value: number) => (total ? (value / total) * 100 : 0);

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      action={action}
      height={height}
      loading={loading}
      isEmpty={items.length === 0}
      emptyMessage={emptyMessage || t("Nothing recorded in the selected period.")}
    >
      <PieChart
        height={height}
        margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
        series={[
          {
            data,
            innerRadius: 46,
            paddingAngle: 2,
            cornerRadius: 4,
            highlightScope: { fade: "global", highlight: "item" },
            // Selective direct labels: a share is written on a slice only when
            // the slice is wide enough to hold it, so nothing is drawn on the
            // slivers and no label overlaps its neighbour.
            arcLabel: (item) => `${Math.round(share(item.value))}%`,
            arcLabelMinAngle: 30,
            arcLabelRadius: "70%",
            valueFormatter: (item: { value: number }) =>
              `${valueFormatter(item.value)} (${share(item.value).toFixed(1)}%)`,
          },
        ]}
        slotProps={{
          legend: {
            direction: "vertical",
            position: { vertical: "middle", horizontal: "end" },
            sx: { gap: 0.5 },
          },
        }}
        sx={{
          ...chartSx,
          // A 2px surface gap between slices rather than a drawn border.
          "& .MuiPieArc-root": { stroke: palette.surface, strokeWidth: 2 },
          // An arc label sits on whichever fill its slice happens to have, and
          // those run from dark blue to pale yellow - no single ink is legible
          // on all of them. White text over a dark halo is, and the halo is
          // painted under the glyph so it never thickens the letterforms.
          "& .MuiPieArcLabel-root": {
            fill: "#ffffff",
            stroke: "rgba(0, 0, 0, 0.55)",
            strokeWidth: 2.5,
            strokeLinejoin: "round",
            paintOrder: "stroke",
            fontSize: 12,
            fontWeight: 700,
          },
        }}
      />
    </ChartCard>
  );
}
